import { transformCompanions } from "@gettactic/map-builder/src/components/map/helpers/transformCompanions";
import type {
  GroupShape,
  Shape,
  SpotShape,
  WallPathShape,
  WallShape
} from "@gettactic/map-builder/src/types";
import { getRandomColor } from "@gettactic/map-builder/src/utils/colors";
import { shiftShapes } from "@gettactic/map-builder/src/utils/computeShapesBounds";
import { generateShapeId } from "@gettactic/map-builder/src/utils/generateShapeId";
import {
  calculateGroupBounds,
  flattenGroup
} from "@gettactic/map-builder/src/utils/transform";
import type { Rect, ShapeById } from "../types";
import { getShapeAabb, rectsIntersect } from "./bounds";

/** The mutable part of the document every operation works on. */
export interface DocumentState {
  shapes: ShapeById;
  order: string[];
}

export const shiftShape = (shape: Shape, dx: number, dy: number): Shape =>
  shiftShapes([shape], dx, dy)[0];

export const moveShapes = (
  document: DocumentState,
  ids: string[],
  dx: number,
  dy: number
): DocumentState => {
  if ((dx === 0 && dy === 0) || ids.length === 0) return document;
  const shapes = { ...document.shapes };
  for (const id of ids) {
    const shape = shapes[id];
    if (!shape || shape.locked) continue;
    shapes[id] = shiftShape(shape, dx, dy);
  }
  return { ...document, shapes };
};

/** Patches a top-level shape, or a shape nested in a group's children. */
export const updateShape = (
  document: DocumentState,
  id: string,
  patch: Partial<Shape>
): DocumentState => {
  const existing = document.shapes[id];
  if (existing) {
    return {
      ...document,
      shapes: { ...document.shapes, [id]: { ...existing, ...patch } as Shape }
    };
  }
  const shapes = { ...document.shapes };
  let changed = false;
  for (const topId of document.order) {
    const shape = shapes[topId];
    if (shape?.type !== "group") continue;
    const children = patchInChildren(shape.children, id, patch);
    if (children !== shape.children) {
      shapes[topId] = { ...shape, children };
      changed = true;
    }
  }
  return changed ? { ...document, shapes } : document;
};

const patchInChildren = (
  children: Shape[],
  id: string,
  patch: Partial<Shape>
): Shape[] => {
  let changed = false;
  const next = children.map((child) => {
    if (child.id === id) {
      changed = true;
      return { ...child, ...patch } as Shape;
    }
    if (child.type === "group") {
      const nested = patchInChildren(child.children, id, patch);
      if (nested !== child.children) {
        changed = true;
        return { ...child, children: nested };
      }
    }
    return child;
  });
  return changed ? next : children;
};

export const deleteShapes = (
  document: DocumentState,
  ids: string[]
): DocumentState => {
  if (ids.length === 0) return document;
  const removed = new Set(ids);
  const shapes = { ...document.shapes };
  for (const id of ids) delete shapes[id];
  return {
    shapes,
    order: document.order.filter((id) => !removed.has(id))
  };
};

export const addShapes = (
  document: DocumentState,
  added: Shape[]
): DocumentState => {
  if (added.length === 0) return document;
  const shapes = { ...document.shapes };
  const order = [...document.order];
  for (const shape of added) {
    if (!(shape.id in shapes)) order.push(shape.id);
    shapes[shape.id] = shape;
  }
  return { shapes, order };
};

export type ZOrderDirection = "up" | "down" | "front" | "back";

/** Same semantics as the Konva editor's `moveShape`: one step swaps
 *  with the neighbour in the saved array; front/back go to the ends. */
export const reorderShapes = (
  document: DocumentState,
  ids: string[],
  direction: ZOrderDirection
): DocumentState => {
  const selected = new Set(ids);
  let order = [...document.order];
  if (direction === "front" || direction === "back") {
    const moving = order.filter((id) => selected.has(id));
    const rest = order.filter((id) => !selected.has(id));
    order = direction === "front" ? [...rest, ...moving] : [...moving, ...rest];
    return { ...document, order };
  }
  if (direction === "up") {
    for (let index = order.length - 2; index >= 0; index--) {
      if (selected.has(order[index]) && !selected.has(order[index + 1])) {
        [order[index], order[index + 1]] = [order[index + 1], order[index]];
      }
    }
  } else {
    for (let index = 1; index < order.length; index++) {
      if (selected.has(order[index]) && !selected.has(order[index - 1])) {
        [order[index], order[index - 1]] = [order[index - 1], order[index]];
      }
    }
  }
  return { ...document, order };
};

export interface DuplicateResult {
  document: DocumentState;
  newIds: string[];
}

export type DuplicateOverride =
  | Partial<Shape>
  | (Partial<Shape> & Required<Pick<Shape, "id">>)[];

export interface DuplicateOptions {
  offset?: { x: number; y: number };
  /** Patch applied to every copy, or one patch per source id. */
  override?: DuplicateOverride;
  /** Single spot: the copy keeps the resource and the original loses it. */
  switchResourceId?: boolean;
  /** Single shape: the copy goes right after the original in z-order. */
  switchIndex?: boolean;
}

const overrideFor = (
  override: DuplicateOverride | undefined,
  shapeId: string
): Partial<Shape> | undefined => {
  if (!override) return undefined;
  if (Array.isArray(override)) {
    return override.find((entry) => entry.id === shapeId);
  }
  return override;
};

const withFreshIds = (
  shape: Shape,
  dx: number,
  dy: number,
  override?: Partial<Shape>
): Shape => {
  const moved = shiftShape(shape, dx, dy);
  const copy = {
    ...moved,
    ...override,
    id: generateShapeId(shape.type)
  } as Shape;
  if (copy.type === "spot" && shape.type === "spot") {
    let companions = shape.companions?.map((companion) => ({
      ...companion,
      id: generateShapeId(companion.type)
    })) as SpotShape["companions"];
    // Turning a desk around mirrors its companions, unless a template
    // fixes their layout.
    if (
      companions?.length &&
      copy.orientation !== shape.orientation &&
      !shape.compositeTemplateId
    ) {
      companions = transformCompanions(
        companions,
        shape.width,
        shape.height,
        shape.orientation,
        copy.orientation
      );
    }
    copy.companions = companions;
  }
  if (copy.type === "group") {
    copy.children = copy.children.map((child) => withFreshIds(child, 0, 0));
  }
  return copy;
};

export const duplicateShapes = (
  document: DocumentState,
  ids: string[],
  options: DuplicateOptions = {}
): DuplicateResult => {
  const offset = options.offset ?? { x: 20, y: 20 };
  const isSingle = ids.length === 1;
  const copies: { source: string; copy: Shape }[] = [];
  for (const id of document.order) {
    if (!ids.includes(id)) continue;
    const shape = document.shapes[id];
    if (!shape) continue;
    const copy = withFreshIds(
      shape,
      offset.x,
      offset.y,
      overrideFor(options.override, id)
    );
    if (copy.type === "spot") {
      // A duplicated spot is a new resource unless the copy takes over.
      copy.resource_id =
        options.switchResourceId && isSingle && shape.type === "spot"
          ? shape.resource_id
          : undefined;
    }
    copies.push({ source: id, copy });
  }
  if (copies.length === 0) return { document, newIds: [] };

  let shapes = { ...document.shapes };
  let order = [...document.order];
  if (options.switchResourceId && isSingle) {
    const original = shapes[ids[0]];
    if (original?.type === "spot") {
      shapes[ids[0]] = { ...original, resource_id: undefined };
    }
  }
  for (const { source, copy } of copies) {
    shapes[copy.id] = copy;
    if (options.switchIndex && isSingle) {
      const index = order.indexOf(source);
      order.splice(index + 1, 0, copy.id);
    } else {
      order.push(copy.id);
    }
  }
  shapes = { ...shapes };
  order = [...order];
  return {
    document: { shapes, order },
    newIds: copies.map((entry) => entry.copy.id)
  };
};

/** Zones stay top-level (they are regions of the floor, not furniture). */
export const groupShapes = (
  document: DocumentState,
  ids: string[]
): { document: DocumentState; groupId: string | null } => {
  const members = document.order
    .filter((id) => ids.includes(id))
    .map((id) => document.shapes[id])
    .filter((shape): shape is Shape => !!shape && shape.type !== "zone");
  if (members.length < 2) return { document, groupId: null };

  const flattened = members.flatMap((shape) =>
    shape.type === "group" ? flattenGroup(shape) : [shape]
  );
  const bounds = calculateGroupBounds(flattened);
  const group: GroupShape = {
    id: generateShapeId("group"),
    type: "group",
    x: bounds.minX,
    y: bounds.minY,
    width: bounds.maxX - bounds.minX,
    height: bounds.maxY - bounds.minY,
    color: getRandomColor(),
    children: members.map((shape) =>
      shiftShape(shape, -bounds.minX, -bounds.minY)
    ),
    layerType: "shapes"
  };
  const without = deleteShapes(
    document,
    members.map((shape) => shape.id)
  );
  return { document: addShapes(without, [group]), groupId: group.id };
};

export const ungroupShape = (
  document: DocumentState,
  groupId: string
): { document: DocumentState; childIds: string[] } => {
  const group = document.shapes[groupId];
  if (group?.type !== "group") return { document, childIds: [] };
  const children = group.children.map((child) =>
    shiftShape(child, group.x, group.y)
  );
  const without = deleteShapes(document, [groupId]);
  return {
    document: addShapes(without, children),
    childIds: children.map((child) => child.id)
  };
};

const segmentIntersectsBox = (
  from: { x: number; y: number },
  to: { x: number; y: number },
  half: number,
  box: Rect
): boolean => {
  const left = box.x - half;
  const top = box.y - half;
  const right = box.x + box.width + half;
  const bottom = box.y + box.height + half;
  if (Math.max(from.x, to.x) < left || Math.min(from.x, to.x) > right) {
    return false;
  }
  if (Math.max(from.y, to.y) < top || Math.min(from.y, to.y) > bottom) {
    return false;
  }
  const inside = (point: { x: number; y: number }) =>
    point.x >= left && point.x <= right && point.y >= top && point.y <= bottom;
  if (inside(from) || inside(to)) return true;
  const edges: [number, number, number, number][] = [
    [left, top, right, top],
    [left, bottom, right, bottom],
    [left, top, left, bottom],
    [right, top, right, bottom]
  ];
  const d1x = to.x - from.x;
  const d1y = to.y - from.y;
  for (const [ax, ay, bx, by] of edges) {
    const d2x = bx - ax;
    const d2y = by - ay;
    const cross = d1x * d2y - d1y * d2x;
    if (Math.abs(cross) < 1e-10) continue;
    const t = ((ax - from.x) * d2y - (ay - from.y) * d2x) / cross;
    const u = ((ax - from.x) * d1y - (ay - from.y) * d1x) / cross;
    if (t >= 0 && t <= 1 && u >= 0 && u <= 1) return true;
  }
  return false;
};

/** Marquee hit test — walls test their actual segments, everything else
 *  its rotation-aware bounding box. Locked and hidden shapes never match,
 *  nor do zones while `excludeZones` (the zones are switched off). */
export const shapesInBox = (
  document: DocumentState,
  box: Rect,
  options: { spotsOnly?: boolean; excludeZones?: boolean } = {}
): string[] => {
  const hits: string[] = [];
  for (const id of document.order) {
    const shape = document.shapes[id];
    if (!shape || shape.locked || shape.hidden) continue;
    if (options.spotsOnly && shape.type !== "spot") continue;
    if (options.excludeZones && shape.type === "zone") continue;
    if (shape.type === "wall") {
      const wall = shape as WallShape;
      const half = (wall.thickness || 10) / 2;
      if (
        segmentIntersectsBox(
          { x: wall.x, y: wall.y },
          { x: wall.x2, y: wall.y2 },
          half,
          box
        )
      ) {
        hits.push(id);
      }
      continue;
    }
    if (shape.type === "wall-path") {
      const path = shape as WallPathShape;
      const half = (path.thickness || 10) / 2;
      const edgeCount = path.closed
        ? path.vertices.length
        : path.vertices.length - 1;
      for (let index = 0; index < edgeCount; index++) {
        const from = path.vertices[index];
        const to = path.vertices[(index + 1) % path.vertices.length];
        if (segmentIntersectsBox(from, to, half, box)) {
          hits.push(id);
          break;
        }
      }
      continue;
    }
    if (rectsIntersect(getShapeAabb(shape), box)) hits.push(id);
  }
  return hits;
};

/**
 * Konva rotates about the shape origin; keep a chosen point of the shape
 * (given in the shape's own unrotated frame, e.g. the centre of its
 * visible box) fixed on screen while the angle changes, so the shape does
 * not swing around a corner on release.
 */
export const rotateShapeAboutPoint = (
  shape: Shape,
  rotation: number,
  localPivot: { x: number; y: number }
): Partial<Shape> => {
  const dx = localPivot.x - shape.x;
  const dy = localPivot.y - shape.y;
  const before = ((shape.rotation || 0) * Math.PI) / 180;
  const after = (rotation * Math.PI) / 180;
  const pivotX = shape.x + Math.cos(before) * dx - Math.sin(before) * dy;
  const pivotY = shape.y + Math.sin(before) * dx + Math.cos(before) * dy;
  return {
    rotation,
    x: pivotX - (Math.cos(after) * dx - Math.sin(after) * dy),
    y: pivotY - (Math.sin(after) * dx + Math.cos(after) * dy)
  };
};

/** Rotation that keeps the centre of the width/height box in place. */
export const rotateShapeAboutCentre = (
  shape: Shape,
  rotation: number
): Partial<Shape> =>
  rotateShapeAboutPoint(shape, rotation, {
    x: shape.x + shape.width / 2,
    y: shape.y + shape.height / 2
  });
